<script setup lang="ts">
import FollowAPI from "~/services/follow";
import TeamsAPI from "~/services/teams";
import useUserStore from "~/stores/user";

const { t } = useI18n();
const userStore = useUserStore();

const emit = defineEmits<{
  navigate: [path: string];
}>();

// Use useAsyncData to automatically load followed items when user becomes authenticated
// This follows the same pattern as useUserSubscription composable
const { pending: isLoadingFollowedItems } = useAsyncData(
  "menu-following-items",
  async () => {
    // Only load if user is authenticated and we don't have items yet
    if (userStore.isAuthenticated && userStore.followedItems.length === 0) {
      await FollowAPI.load();
    }
    return userStore.followedItems;
  },
  {
    getCachedData: () => {
      // If we already have followed items in the store, use them (no need to fetch)
      if (userStore.followedItems.length > 0) {
        return userStore.followedItems;
      }
      // If user is not authenticated, return empty array (no need to fetch)
      if (!userStore.isAuthenticated) {
        return [];
      }
      return undefined;
    },
    watch: [() => userStore.isAuthenticated],
  },
);

// Load teams data to get team colors (team color is not stored in followed items)
const { data: teamsResponse } = useAsyncData(
  "menu-following-teams",
  () => TeamsAPI.load({
    pageSize: 200,
  }),
  {
    getCachedData: key => useNuxtApp().payload.data[key] || useNuxtApp().static.data[key],
  },
);

const followedTeams = computed(() => {
  // Show all teams, regardless of whether they have jersey numbers
  return userStore.followedItems
    .filter((item: IModelFollowedItem) => item.type === "team")
    .slice(0, 2);
});

const followedPlayers = computed(() => {
  const teamsWithPlayers = userStore.followedItems
    .filter((item: IModelFollowedItem) => item.type === "team" && item.jerseyNumbers && item.jerseyNumbers.length > 0);

  // Get team details from teamsResponse if available
  const teamDetailsMap = new Map<string, { color?: string; title?: string }>();
  if (teamsResponse.value?.content?.entries) {
    teamsResponse.value.content.entries.forEach((team) => {
      teamDetailsMap.set(String(team.id), {
        color: team.color,
        title: team.title,
      });
    });
  }

  const players: Array<{ teamId: string; teamTitle: string; jerseyNumber: number; teamColor?: string }> = [];

  teamsWithPlayers.forEach((team) => {
    const details = teamDetailsMap.get(team.id);
    team.jerseyNumbers?.forEach((jerseyNumber) => {
      players.push({
        teamId: team.id,
        teamTitle: details?.title || team.title,
        jerseyNumber,
        teamColor: details?.color || "#D0142C", // Use actual team color or fallback to default
      });
    });
  });

  return players.slice(0, 2);
});

const hasFollowingItems = computed(() => {
  return followedTeams.value.length > 0 || followedPlayers.value.length > 0;
});

const teamsJustifyClass = computed(() => {
  // Total items = teams + Add button
  const totalItems = followedTeams.value.length + 1;
  return totalItems > 2 ? "justify-between" : "justify-start";
});

const playersJustifyClass = computed(() => {
  // Total items = players + Add button
  const totalItems = followedPlayers.value.length + 1;
  return totalItems > 2 ? "justify-between" : "justify-start";
});

function handleSeeAll() {
  emit("navigate", "/following/?tab=teams");
}

function handleAddTeam() {
  emit("navigate", "/following/?tab=teams");
}

function handleAddPlayer() {
  emit("navigate", "/following/?tab=players");
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 class="text-lg sm:text-xl font-bold text-[var(--base-text-color)]">
        {{ t('labels.following') }}
      </h2>
      <BaseButton
        v-if="hasFollowingItems"
        name="see-all-following"
        class="text-heading-4"
        link
        @click="handleSeeAll"
      >
        <div class="flex items-center justify-center gap-2">
          {{ t('labels.see_all') }}
          <IconChevronRight class="inline" :height="8" :width="8" />
        </div>
      </BaseButton>
    </div>

    <!-- Content Card -->
    <div class="bg-[#F8F8F8] rounded-xl p-4">
      <!-- Loading State -->
      <template v-if="isLoadingFollowedItems">
        <!-- Teams Section Skeleton -->
        <div class="mb-6 last:mb-0">
          <h3 class="text-xs font-bold text-[#6C7784] mb-3">
            {{ t('labels.teams') }}
          </h3>
          <div class="flex justify-start gap-2">
            <template v-for="i in 2" :key="`team-skeleton-${i}`">
              <div
                class="flex flex-col items-center gap-2 w-[80px] sm:w-[100px]"
              >
                <div
                  class="
                    w-14 h-14 rounded-full overflow-hidden bg-white
                    outline outline-1 outline-[var(--base-border-color,#dfdfdf)] outline-offset-[-1px]
                    flex items-center justify-center flex-shrink-0
                  "
                >
                  <div class="skeletonLoading w-14 h-14 rounded-full" />
                </div>
                <div class="skeletonLoading h-3 w-full rounded" />
              </div>
            </template>
            <button
              type="button"
              class="flex flex-col items-center gap-2 cursor-not-allowed w-[80px] sm:w-[100px] opacity-50"
              disabled
            >
              <IconAdd class="w-14 h-14" />
              <p
                class="text-center text-[#1A1C1E] font-medium text-xs"
              >
                Add
              </p>
            </button>
          </div>
        </div>

        <!-- Players Section Skeleton -->
        <div>
          <h3 class="text-xs font-bold text-[#6C7784] mb-3">
            Players
          </h3>
          <div class="flex justify-start gap-2">
            <template v-for="i in 2" :key="`player-skeleton-${i}`">
              <div
                class="flex flex-col items-center gap-2 w-[80px] sm:w-[100px]"
              >
                <div
                  class="
                    w-14 h-14 rounded-full overflow-hidden bg-white
                    outline outline-1 outline-[var(--base-border-color,#dfdfdf)] outline-offset-[-1px]
                    flex items-center justify-center flex-shrink-0 relative pointer-events-none
                  "
                >
                  <div class="skeletonLoading w-14 h-14 rounded-full" />
                </div>
                <div class="skeletonLoading h-3 w-full rounded" />
              </div>
            </template>
            <button
              type="button"
              class="flex flex-col items-center gap-2 cursor-not-allowed w-[80px] sm:w-[100px] opacity-50"
              disabled
            >
              <IconAdd class="w-14 h-14" />
              <p
                class="text-center text-[#1A1C1E] font-medium text-xs"
              >
                Add
              </p>
            </button>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div v-else-if="!hasFollowingItems" class="flex flex-col items-center justify-center">
        <button
          type="button"
          class="flex flex-col items-center gap-2 cursor-pointer"
          @click="handleAddTeam"
        >
          <IconAdd class="w-14 h-14" />
          <p
            class="text-center text-[#1A1C1E]"
            style="font-family: 'Red Hat Display', sans-serif; font-weight: 500; font-size: 12px; line-height: 100%; letter-spacing: 0px;"
          >
            Add team/player
          </p>
        </button>
      </div>

      <!-- Content with Items -->
      <template v-else>
        <!-- Teams Section -->
        <div class="mb-6 last:mb-0">
          <h3 class="text-xs font-bold text-[#6C7784] mb-3">
            {{ t('labels.teams') }}
          </h3>
          <div :class="`flex ${teamsJustifyClass} gap-2`">
            <template v-for="team in followedTeams" :key="team.id">
              <div
                class="flex flex-col items-center gap-2 w-[80px] sm:w-[100px]"
              >
                <div
                  class="
                    w-14 h-14 rounded-full overflow-hidden bg-white
                    outline outline-1 outline-[var(--base-border-color,#dfdfdf)] outline-offset-[-1px]
                    flex items-center justify-center flex-shrink-0
                  "
                >
                  <BaseLogo
                    :src="team.imageUrl"
                    :alt="team.title"
                    :title="team.title"
                    :width="38"
                    :height="38"
                    :with-initials="true"
                    fit="contain"
                  />
                </div>
                <p
                  class="text-center text-[#1A1C1E] font-medium text-xs w-full line-clamp-2"
                >
                  {{ team.title }}
                </p>
              </div>
            </template>
            <button
              type="button"
              class="flex flex-col items-center gap-2 cursor-pointer w-[80px] sm:w-[100px]"
              @click="handleAddTeam"
            >
              <IconAdd class="w-14 h-14" />
              <p
                class="text-center text-[#1A1C1E] font-medium text-xs"
              >
                Add
              </p>
            </button>
          </div>
        </div>

        <!-- Players Section -->
        <div>
          <h3 class="text-xs font-bold text-[#6C7784] mb-3">
            Players
          </h3>
          <div :class="`flex ${playersJustifyClass} gap-2`">
            <template v-for="(player, index) in followedPlayers" :key="`${player.teamId}-${player.jerseyNumber}-${index}`">
              <div
                class="flex flex-col items-center gap-2 w-[80px] sm:w-[100px]"
              >
                <div
                  class="
                    w-14 h-14 rounded-full overflow-hidden bg-white
                    outline outline-1 outline-[var(--base-border-color,#dfdfdf)] outline-offset-[-1px]
                    flex items-center justify-center flex-shrink-0 relative pointer-events-none
                  "
                >
                  <div class="scale-[0.51] origin-center pointer-events-none">
                    <CardPlayerJersey
                      :player-number="player.jerseyNumber"
                      :color="player.teamColor || '#D0142C'"
                      :is-selected="false"
                    />
                  </div>
                </div>
                <p
                  class="text-center text-[#1A1C1E] font-medium text-xs w-full line-clamp-2"
                >
                  {{ player.teamTitle }} #{{ player.jerseyNumber }}
                </p>
              </div>
            </template>
            <button
              type="button"
              class="flex flex-col items-center gap-2 cursor-pointer w-[80px] sm:w-[100px]"
              @click="handleAddPlayer"
            >
              <IconAdd class="w-14 h-14" />
              <p
                class="text-center text-[#1A1C1E] font-medium text-xs"
              >
                Add
              </p>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
